import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import usePerx3Redesign from 'hooks/usePerx3Redesign';

import { useOrbitContext } from '../OrbitContext';
import { useOrbitNotice } from '../OrbitNoticeContext';
import { OrbitGlyphSplit, OrbitGlyphIcon, splitOrbitGlyph } from '../glyphIcons';
import { TOrbit } from '../i18n/TModules';
import OrbitToastBubble from './OrbitToastBubble';

/**
 * No frame and none of the file's 488 comments state a dismissal time, so this is ours: long enough
 * to read a one-line sentence twice, and answerable — the bubble carries a close control and the
 * countdown stops while the pointer or the keyboard is on it.
 */
export const ORBIT_NOTICE_DISMISS_MS = 6000;

/**
 * The bubble is drawn at y 70 in all three frames - 8px under the 62-high `op-head` - floating over
 * whatever body is on screen, home panel or running transcript alike. It is drawn INSIDE the panel:
 * the host portals into the panel node the sidebar publishes, so the bubble's box is the panel's
 * box and it cannot land beside or over the page the panel sits next to.
 *
 * z-index 3 clears both layers it has to sit over inside the panel: the sticky context block (1)
 * and the conflict scrim (2). Neither `OrbitBodyArea` nor `OrbitBodyUnderlay` opens a stacking
 * context - both are `position: relative` at `z-index: auto` - so all three numbers are compared in
 * the panel's own context.
 *
 * The 15px side gutters are the frame's: the widest drawn bubble (`5140:1485`) is 460 in a 489
 * panel, leaving 15 and 14 at its sides. A subject longer than the frame's own, or a longer
 * translation, wraps to a second line inside those gutters rather than growing past them.
 */
const Host = styled.div`
  position: absolute;
  top: 70px;
  right: 15px;
  left: 15px;
  z-index: 3;
  display: flex;
  justify-content: center;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`;

const OrbitNoticeHost: React.FC = () => {
  const { t } = useMultiLanguageTranslation();
  const { open } = useOrbitContext();
  const { notice, dismissNotice, anchor } = useOrbitNotice();
  const perx3 = usePerx3Redesign();
  const [held, setHeld] = useState(false);
  const noticeId = notice?.id;

  useEffect(() => setHeld(false), [noticeId]);

  useEffect(() => {
    if (noticeId === undefined || held) {
      return undefined;
    }

    const timer = setTimeout(dismissNotice, ORBIT_NOTICE_DISMISS_MS);

    return () => clearTimeout(timer);
  }, [noticeId, held, dismissNotice]);

  // Closing the panel takes the notice with it: every one of them reports on the investigation the
  // panel is showing, and re-opening days later to a stale toast states something no longer true.
  useEffect(() => {
    if (!open) {
      dismissNotice();
    }
  }, [open, dismissNotice]);

  // No panel on screen, no notice: every one of them reports on the investigation the panel is
  // showing, and the bubble is positioned against the panel's own box.
  if (!open || notice === null || anchor === null) {
    return null;
  }

  const copy = t(`${TOrbit}.${notice.messageKey}`, notice.params);
  const message: OrbitGlyphSplit = perx3 ? splitOrbitGlyph(copy) : { rest: copy };

  return createPortal(
    <Host
      data-testid="orbit-notice-host"
      onMouseEnter={() => setHeld(true)}
      onMouseLeave={() => setHeld(false)}
      onFocus={() => setHeld(true)}
      onBlur={() => setHeld(false)}
    >
      <OrbitToastBubble
        tone={notice.tone}
        message={message.rest}
        glyph={message.Icon !== undefined ? <OrbitGlyphIcon Icon={message.Icon} /> : notice.glyph}
        onDismiss={dismissNotice}
        dismissLabel={t(`${TOrbit}.toast.dismiss`)}
      />
    </Host>,
    anchor
  );
};

export default OrbitNoticeHost;
